if (!customElements.get('neya-herbier')) {
  class NeyaHerbier extends HTMLElement {
    constructor() {
      super();
      this.activeBenefit = '';
      this.activeCard = null;
      this.lastFocusedElement = null;
      this.visibleCards = [];
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.searchInput = this.querySelector('[data-herbier-search]');
      this.clearButton = this.querySelector('[data-herbier-clear]');
      this.resetButton = this.querySelector('[data-herbier-reset]');
      this.filtersContainer = this.querySelector('[data-herbier-filters]');
      this.resultCount = this.querySelector('[data-herbier-count]');
      this.emptyState = this.querySelector('[data-herbier-empty]');
      this.cards = Array.from(this.querySelectorAll('[data-herbier-card]'));
      this.drawer = this.querySelector('[data-herbier-drawer]');
      this.drawerPanel = this.querySelector('[data-herbier-panel]');
      this.drawerContent = this.querySelector('[data-herbier-drawer-content]');
      this.drawerPosition = this.querySelector('[data-herbier-position]');
      this.previousButton = this.querySelector('[data-herbier-previous]');
      this.nextButton = this.querySelector('[data-herbier-next]');
      this.closeButton = this.querySelector('.neya-herbier-drawer__close');
      this.maxFilters = Number.parseInt(this.dataset.maxFilters || '10', 10);
      this.maxCardBenefits = Number.parseInt(this.dataset.maxCardBenefits || '3', 10);
      this.resultSingular = this.dataset.resultSingular || 'ingrédient';
      this.resultPlural = this.dataset.resultPlural || 'ingrédients';

      this.prepareBenefits();
      this.bindEvents();
      this.applyFilters();
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr')
        .trim();
    }

    cleanBenefit(value) {
      return String(value || '')
        .trim()
        .replace(/^[\s[\]"']+|[\s\[\]"']+$/g, '')
        .trim();
    }

    parseBenefits(value) {
      const rawValue = String(value || '').trim();
      if (!rawValue) return [];

      let items = [];

      if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
        try {
          const parsedValue = JSON.parse(rawValue);
          if (Array.isArray(parsedValue)) items = parsedValue;
        } catch (error) {
          items = [];
        }
      }

      if (!items.length) {
        items = rawValue.split(/[•·|,;\n]+/);
      }

      const uniqueBenefits = new Map();
      items.forEach((item) => {
        const label = this.cleanBenefit(item);
        const key = this.normalize(label);
        if (label && key && !uniqueBenefits.has(key)) {
          uniqueBenefits.set(key, label);
        }
      });

      return Array.from(uniqueBenefits.values());
    }

    renderBenefitChips(container, benefits, limit = benefits.length) {
      if (!container) return;
      container.replaceChildren();

      benefits.slice(0, limit).forEach((label) => {
        const chip = document.createElement('span');
        chip.className = 'neya-herbier-chip';
        chip.textContent = label;
        container.appendChild(chip);
      });

      container.hidden = benefits.length === 0;
    }

    prepareBenefits() {
      const benefits = new Map();

      this.cards.forEach((card) => {
        const cardBenefits = this.parseBenefits(card.dataset.benefits);
        card.dataset.benefitKeys = cardBenefits.map((item) => this.normalize(item)).join('|');

        const chips = card.querySelector('[data-herbier-card-chips]');
        this.renderBenefitChips(chips, cardBenefits, this.maxCardBenefits);

        cardBenefits.forEach((label) => {
          const key = this.normalize(label);
          if (!key) return;
          const current = benefits.get(key);
          benefits.set(key, {
            key,
            label: current?.label || label,
            count: (current?.count || 0) + 1,
          });
        });
      });

      if (!this.filtersContainer) return;

      const sortedBenefits = Array.from(benefits.values())
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'fr'))
        .slice(0, this.maxFilters);

      sortedBenefits.forEach((benefit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'neya-herbier-filter';
        button.dataset.herbierFilter = benefit.key;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = benefit.label;
        this.filtersContainer.appendChild(button);
      });

      this.filtersContainer.hidden = sortedBenefits.length === 0;
    }

    bindEvents() {
      this.searchInput?.addEventListener('input', () => this.applyFilters());
      this.clearButton?.addEventListener('click', () => {
        if (this.searchInput) {
          this.searchInput.value = '';
          this.searchInput.focus();
        }
        this.applyFilters();
      });

      this.resetButton?.addEventListener('click', () => this.resetFilters());

      this.filtersContainer?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-herbier-filter]');
        if (!button) return;
        this.activeBenefit = button.dataset.herbierFilter || '';
        this.updateFilterButtons();
        this.applyFilters();
      });

      this.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-herbier-open]');
        if (openButton) {
          this.openCard(openButton.closest('[data-herbier-card]'));
          return;
        }

        if (event.target.closest('[data-herbier-close]')) {
          this.closeDrawer();
        }
      });

      this.previousButton?.addEventListener('click', () => this.navigateDrawer(-1));
      this.nextButton?.addEventListener('click', () => this.navigateDrawer(1));
    }

    updateFilterButtons() {
      this.querySelectorAll('[data-herbier-filter]').forEach((button) => {
        const active = (button.dataset.herbierFilter || '') === this.activeBenefit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    applyFilters() {
      const query = this.normalize(this.searchInput?.value);

      this.visibleCards = this.cards.filter((card) => {
        const searchMatches = !query || this.normalize(card.dataset.search).includes(query);
        const benefitKeys = (card.dataset.benefitKeys || '').split('|');
        const benefitMatches = !this.activeBenefit || benefitKeys.includes(this.activeBenefit);
        const visible = searchMatches && benefitMatches;
        card.hidden = !visible;
        return visible;
      });

      const resultLabel = this.visibleCards.length === 1 ? this.resultSingular : this.resultPlural;
      if (this.resultCount) {
        this.resultCount.textContent = `${this.visibleCards.length} ${resultLabel}`;
      }

      if (this.emptyState) {
        this.emptyState.hidden = this.visibleCards.length !== 0;
      }

      if (this.clearButton) {
        this.clearButton.hidden = !query;
      }

      if (this.resetButton) {
        this.resetButton.hidden = !query && !this.activeBenefit;
      }

      if (this.drawer?.classList.contains('is-open')) {
        this.updateDrawerNavigation();
      }
    }

    resetFilters() {
      if (this.searchInput) this.searchInput.value = '';
      this.activeBenefit = '';
      this.updateFilterButtons();
      this.applyFilters();
      this.searchInput?.focus();
    }

    openCard(card) {
      if (!card || !this.drawer || !this.drawerContent) return;
      const templateId = card.dataset.templateId;
      const template = templateId ? this.querySelector(`#${CSS.escape(templateId)}`) : null;
      if (!template) return;

      window.clearTimeout(this.closeTimer);
      this.activeCard = card;
      if (!this.drawer.classList.contains('is-open')) {
        this.lastFocusedElement = document.activeElement;
      }
      this.drawerContent.replaceChildren(template.content.cloneNode(true));
      const drawerChips = this.drawerContent.querySelector('[data-herbier-drawer-chips]');
      if (drawerChips) {
        const drawerBenefits = this.parseBenefits(drawerChips.dataset.benefits);
        this.renderBenefitChips(drawerChips, drawerBenefits);
      }
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('neya-herbier-drawer-open');
      document.addEventListener('keydown', this.onKeydown);
      this.updateDrawerNavigation();

      window.requestAnimationFrame(() => this.closeButton?.focus());
    }

    closeDrawer() {
      if (!this.drawer?.classList.contains('is-open')) return;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('neya-herbier-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);

      const focusTarget = this.lastFocusedElement;
      this.closeTimer = window.setTimeout(() => {
        if (this.drawerContent) this.drawerContent.replaceChildren();
        if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) {
          focusTarget.focus();
        }
        this.activeCard = null;
      }, 280);
    }

    navigateDrawer(direction) {
      if (!this.activeCard || !this.visibleCards.length) return;
      const currentIndex = this.visibleCards.indexOf(this.activeCard);
      const targetCard = this.visibleCards[currentIndex + direction];
      if (targetCard) this.openCard(targetCard);
    }

    updateDrawerNavigation() {
      const currentIndex = this.visibleCards.indexOf(this.activeCard);
      const position = currentIndex >= 0 ? currentIndex + 1 : 0;

      if (this.drawerPosition) {
        this.drawerPosition.textContent = position
          ? `${position} / ${this.visibleCards.length}`
          : '';
      }
      if (this.previousButton) {
        this.previousButton.disabled = currentIndex <= 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled = currentIndex < 0 || currentIndex >= this.visibleCards.length - 1;
      }
    }

    getFocusableElements() {
      if (!this.drawerPanel) return [];
      return Array.from(
        this.drawerPanel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute('hidden'));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('neya-herbier', NeyaHerbier);
}
